import axios, { AxiosError, AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { cookie } from '@sendible/common';

export type ApiMethodsPropType = 'POST' | 'GET' | 'PUT' | 'DELETE';

export const getErrorFromFetchWithHeaders = (error: unknown) => {
  if (error instanceof AxiosError) {
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
  }

  return error;
};

function getHeadersForTestEnvironment(headers: object = {}) {
  const reverseProxyIP = cookie('proxysrv').get();

  return Object.assign(headers, process.env.SENDIBLE_ENV !== 'production' && reverseProxyIP ? { 'X-proxysrv': reverseProxyIP } : null);
}

function getUrlWithSearchParams(url: string, params?: object): string {
  const urlObject = new URL(url);

  if (params) {
    // Add or overwrite search params from the provided `params` object
    Object.entries(params).forEach(([key, value]) => {
      urlObject.searchParams.set(key, value);
    });
  }

  return urlObject.toString();
}

export type FetchWithHeaderParamsType = {
  method: ApiMethodsPropType;
  url: string;
  params?: object;
  body?: object;
  headers?: object;
  onUploadProgress?: (p: number, e: AxiosProgressEvent, currentAbortController: AbortController) => void;
  // Lets a caller cancel this request from the outside (e.g. an in-flight fetch that's no
  // longer relevant because the user navigated away). Falls back to the internal controller
  // used for onUploadProgress when not provided, so existing callers are unaffected.
  signal?: AbortSignal;
};

const fetchWithHeader = async ({ method, url, params, body, headers, onUploadProgress, signal }: FetchWithHeaderParamsType) => {
  const currentAbortController = new AbortController();

  const config: AxiosRequestConfig = {
    method,
    url: getUrlWithSearchParams(url, params),
    headers: getHeadersForTestEnvironment(headers),
    data: body,
    signal: signal ?? currentAbortController.signal,
    onUploadProgress:
      onUploadProgress &&
      ((progressEvent) => {
        let completedPercent = 0;

        if (progressEvent?.total) {
          completedPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }

        onUploadProgress(completedPercent, progressEvent, currentAbortController);
      }),
  };

  return axios(config)
    .then(async (response) => response.data)
    .then((result: unknown) => result);
};

export default fetchWithHeader;
